import React from 'react';
import { graphql, useFragment, useMutation } from 'react-relay';
import { ButtonItem } from '@atlaskit/menu';
import { fg } from '@atlassian/jira-feature-gating';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useFlagsService } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { moveIssueToOtherVersion_softwareReleasesMoveIssuesBetweenVersions_jiraIssue$key } from '@atlassian/jira-relay/src/__generated__/moveIssueToOtherVersion_softwareReleasesMoveIssuesBetweenVersions_jiraIssue.graphql';
import type { moveIssueToOtherVersion_softwareReleasesMoveIssuesBetweenVersions_jiraVersion$key } from '@atlassian/jira-relay/src/__generated__/moveIssueToOtherVersion_softwareReleasesMoveIssuesBetweenVersions_jiraVersion.graphql';
import type { moveIssueToOtherVersion_softwareReleasesMoveIssuesBetweenVersionsMutation } from '@atlassian/jira-relay/src/__generated__/moveIssueToOtherVersion_softwareReleasesMoveIssuesBetweenVersionsMutation.graphql';
import { isDevopsFeatureDisabledInFedRamp } from '@atlassian/jira-software-devops-fedramp-utils/src/index.tsx';
import { useVersionIssuesGraphQLVariables } from '@atlassian/jira-software-releases-version-detail-issue-list-query-param-controller/src/controllers/graphql-variables/index.tsx';
import {
	useMoveIssuesBetweenVersionsExperienceStart,
	useMoveIssuesBetweenVersionsExperienceFail,
	useMoveIssuesBetweenVersionsExperienceSuccess,
} from '../../../services/move-issues-experience-tracker/index.tsx';
import messages from './messages.tsx';
import { getErrorMessage } from './utils.tsx';

type Props = {
	jiraVersion: moveIssueToOtherVersion_softwareReleasesMoveIssuesBetweenVersions_jiraVersion$key;
	jiraIssue: moveIssueToOtherVersion_softwareReleasesMoveIssuesBetweenVersions_jiraIssue$key;
	originalJiraVersion: string;
};

export const MoveIssueToOtherVersion = ({ jiraVersion, jiraIssue, originalJiraVersion }: Props) => {
	const { showFlag } = useFlagsService();
	const { formatMessage } = useIntl();

	const issue = useFragment(
		graphql`
			fragment moveIssueToOtherVersion_softwareReleasesMoveIssuesBetweenVersions_jiraIssue on JiraIssue {
				id @required(action: THROW)
				key @required(action: THROW)
			}
		`,
		jiraIssue,
	);
	const version = useFragment(
		graphql`
			fragment moveIssueToOtherVersion_softwareReleasesMoveIssuesBetweenVersions_jiraVersion on JiraVersion {
				id @required(action: THROW)
				name @required(action: THROW)
			}
		`,
		jiraVersion,
	);

	const [moveIssuesToFixVersion] =
		useMutation<moveIssueToOtherVersion_softwareReleasesMoveIssuesBetweenVersionsMutation>(graphql`
			mutation moveIssueToOtherVersion_softwareReleasesMoveIssuesBetweenVersionsMutation(
				$input: JiraMoveIssuesToFixVersionInput!
				$sortBy: JiraVersionIssuesSortInput
				$filters: JiraVersionIssuesFiltersInput
				$isDevopsFeatureDisabledInFedRamp: Boolean!
				$shouldIncludeDevOpsFieldsViaAgs: Boolean!
			) {
				jira {
					moveIssuesToFixVersion(input: $input) {
						success
						errors {
							message
						}
						issuesWithMissingResolvePermission
						issuesWithMissingEditPermission
						originalVersion {
							...addRelatedWorkPopup_softwareReleasesAddRelatedWorkToVersion_jiraVersion
							...issueTotalCount_softwareReleasesVersionDetailIssueList
							...ui_softwareReleasesVersionDetail_VersionDetailInner_jiraVersion
							...ui_softwareReleasesVersionDetailIssueList_IssuesRefetchable_jiraVersion
						}
					}
				}
			}
		`);
	const { filters, sortBy } = useVersionIssuesGraphQLVariables();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const analyticsEvent = createAnalyticsEvent({
		action: 'clicked',
		actionSubject: 'button',
	});

	const experienceStart = useMoveIssuesBetweenVersionsExperienceStart();
	const experienceFail = useMoveIssuesBetweenVersionsExperienceFail();
	const experienceSuccess = useMoveIssuesBetweenVersionsExperienceSuccess();

	const onError = (err: Error) => {
		showFlag({
			type: 'error',
			title: formatMessage(messages.errorFlagDescription, {
				issueKey: issue.key,
				versionName: version.name,
			}),
			messageId:
				'software-releases-move-issues-between-versions.ui.version-list.move-issue-to-other-version.show-flag.error.on-error.show-flag.error',
			messageType: 'transactional',
		});
		experienceFail('graphql error', err);
		fireErrorAnalytics({
			error: err,
			meta: {
				id: 'jira.releases.remove-issue-from-version.mutation',
				packageName: 'jiraSoftwareReleasesMoveIssuesBetweenVersions',
				teamName: 'nova',
			},
			sendToPrivacyUnsafeSplunk: true,
		});
	};

	const onErrorWithPermissionValidationEnabled = (
		issueKey: string,
		issuesWithMissingResolvePermission: readonly string[] | null | undefined,
		issuesWithMissingEditPermission: readonly string[] | null | undefined,
	) => {
		const error = getErrorMessage(
			issueKey,
			issuesWithMissingResolvePermission,
			issuesWithMissingEditPermission,
			formatMessage,
		);
		showFlag({
			type: 'error',
			title: formatMessage(
				fg('jira-issue-terminology-update-m4')
					? messages.errorFlagTitleIssueTerminologyRefreshM4
					: messages.errorFlagTitle,
			),
			description: error,
			messageId:
				'software-releases-move-issues-between-versions.ui.version-list.move-issue-to-other-version.show-flag.error.on-error-with-permission-validation-enabled.show-flag.error',
			messageType: 'transactional',
		});
		experienceFail('graphql error', new Error(error));
		fireErrorAnalytics({
			error: new Error(error),
			meta: {
				id: 'jira.releases.remove-issue-from-version.mutation',
				packageName: 'jiraSoftwareReleasesMoveIssuesBetweenVersions',
				teamName: 'nova',
			},
			sendToPrivacyUnsafeSplunk: true,
		});
	};

	const onUpdateVersion = () => {
		moveIssuesToFixVersion({
			variables: {
				input: {
					issueIds: [issue.id],
					originalVersionId: originalJiraVersion,
					targetVersionId: version.id,
				},
				isDevopsFeatureDisabledInFedRamp: isDevopsFeatureDisabledInFedRamp(),
				shouldIncludeDevOpsFieldsViaAgs: !isDevopsFeatureDisabledInFedRamp(),
				filters,
				sortBy,
			},
			onCompleted: (data) => {
				if (data?.jira?.moveIssuesToFixVersion?.success !== true) {
					onErrorWithPermissionValidationEnabled(
						issue.key,
						data?.jira?.moveIssuesToFixVersion?.issuesWithMissingResolvePermission,
						data?.jira?.moveIssuesToFixVersion?.issuesWithMissingEditPermission,
					);
				} else {
					const flagText = formatMessage(messages.successFlagDescription, {
						issueKey: issue.key,
						versionName: version.name,
					});
					showFlag({
						type: 'success',
						title: flagText,
						messageId:
							'software-releases-move-issues-between-versions.ui.version-list.move-issue-to-other-version.show-flag.success.on-completed.show-flag.success',
						messageType: 'transactional',
					});
					experienceSuccess();
				}
			},
			onError,
		});
	};

	return (
		<ButtonItem
			onClick={() => {
				fireUIAnalytics(analyticsEvent, 'moveIssueToVersion');
				experienceStart();
				onUpdateVersion();
			}}
		>
			{version.name}
		</ButtonItem>
	);
};
